import React, { useImperativeHandle, useMemo, useRef } from "react";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import { useTranslation } from "react-i18next";
import { ExpandedColumn } from "library/types/columns";
import { useTaxonomy } from "library/utils/useTaxonomy";
import {
  ExportPeriod,
  getExportFileName,
  getExportableColumns,
  isCurrencyColumn,
  isNumberColumn,
  transformOffersForExport,
} from "library/utils/exportOffers";

type Props = {
  columns: ExpandedColumn[];
  rows: any[];
  /** export period, becomes part of the file name */
  period?: ExportPeriod;
};

/** What the page holds a ref to; saving goes through here so invalidated rows get highlighted. */
export type ExportExcelOffersHandle = { save: () => void };

const HEADER_FILL = "#005aa1";
const HEADER_COLOR = "#ffffff";
const INVALIDATED_FILL = "#fed9cc";

/**
 * Hidden Kendo ExcelExport instance. The parent triggers the download via the
 * ref (`ref.current.save()`) once the full offer list has been fetched.
 */
export const ExportExcelOffers = React.forwardRef<
  ExportExcelOffersHandle,
  Props
>(({ columns, rows, period }, ref) => {
  const { t } = useTranslation();
  const taxonomy = useTaxonomy();
  const excelExportRef = useRef<ExcelExport>(null);

  const exportColumns = useMemo(
    () => getExportableColumns(columns),
    [columns]
  );

  const data = useMemo(
    () =>
      transformOffersForExport({
        rows,
        columns: exportColumns,
        taxonomy,
        t,
        target: "excel",
      }),
    [rows, exportColumns, taxonomy, t]
  );

  useImperativeHandle(
    ref,
    () => ({
      save: () => {
        const excelExport = excelExportRef.current;
        if (!excelExport || rows.length === 0) return;
        const workbook = excelExport.workbookOptions();
        const invalidatedStatusId =
          taxonomy?.OfferStatus?.byCode?.["OfferStatus.INVALIDATED"]?.id;
        const dataRows = (workbook.sheets?.[0]?.rows ?? []).filter(
          (r) => r.type === "data"
        );
        dataRows.forEach((sheetRow, index) => {
          // invalidated offers and offers marked as error are shaded
          const isInvalidated =
            invalidatedStatusId !== undefined &&
            rows[index]?.OfferStatus === invalidatedStatusId;
          if (!isInvalidated && !rows[index]?.IsMarkedAsError) return;
          sheetRow.cells?.forEach((cell) => {
            cell.background = INVALIDATED_FILL;
          });
        });
        excelExport.save(workbook);
      },
    }),
    [rows, taxonomy]
  );

  return (
    <ExcelExport
      ref={excelExportRef}
      data={data}
      fileName={getExportFileName("xlsx", period)}
    >
      {exportColumns.map((col) => {
        const width = col.excel?.width ?? Math.max(col.width, 80);
        const numeric = isNumberColumn(col);
        return (
          <ExcelExportColumn
            key={col.key}
            field={col.key}
            title={t(col.labelKey)}
            width={width}
            cellOptions={{
              format: numeric
                ? isCurrencyColumn(col)
                  ? "#,##0.00"
                  : "0"
                : "text",
              textAlign: numeric ? "right" : "left",
            }}
            headerCellOptions={{
              background: HEADER_FILL,
              color: HEADER_COLOR,
              bold: true,
              textAlign: "center",
            }}
          />
        );
      })}
    </ExcelExport>
  );
});

ExportExcelOffers.displayName = "ExportExcelOffers";
